
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Label } from 'src/app/interfaces/api/label';

@Component({
  selector: 'lib-optional-fields',
  imports: [
    CommonModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatCheckboxModule,
    MatSelectModule,
    MatDatepickerModule,
    MatFormFieldModule,
    FormsModule,
    MatInputModule,
    MatOptionModule,
  ],
  templateUrl: './optional-fields.component.html',
  styleUrl: './optional-fields.component.scss'
})
export class OptionalFieldsComponent implements OnChanges {
  @Input('options') options: any[] = [];
  @Input('fields') fields: any[] = [];
  @Input() label: Label | undefined;
  @Input() disabled: boolean = false;
  @Output('change') change: EventEmitter<any> = new EventEmitter<any>();

  dynamicSearchTexts: Record<string, string> = {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["options"]?.currentValue?.length || changes["fields"]) {
      // Render ALL configured fields (not just required ones): the previous
      // required-only filter is why non-required fields (e.g. Description) never showed.
      this.options.forEach(option => {
        let field = this.fields?.find(field => field.key == option.label);
        if (field)
          option.value = ["checkbox", "dynamic"].includes(option.type) ? field.value : field.value[0];
        else if (option.type == "dynamic")
          option.value = undefined;
        if (option.type == "dynamic") {
          let labelOption = this.label ? { name: this.label.name, values: this.label?.optionalFields || [] } : { name: "Undefined", values: [] };
          option.labelOption = labelOption;
        }
      })
    }
    if (changes["label"]?.currentValue) {
      this.options.forEach(option => {
        if (option.type == "dynamic") {
          let labelOption = this.label ? { name: this.label.name, values: this.label?.optionalFields || [] } : { name: "Undefined", values: [] };
          option.labelOption = labelOption;
        }
      });
    }
  }

  /**
   * Whether a field is effectively required for the current label: `dynamic` fields are
   * required whenever the label has values; others need the `required` flag (and, for
   * option-lists, actual options). Drives the required-field styling.
   */
  isRequired(option: any): boolean {
    if (option?.type === 'dynamic') {
      return (this.label?.optionalFields?.length ?? 0) > 0;
    }
    if (!option?.required) return false;
    if (Array.isArray(option.options)) return option.options.length > 0;
    return true;
  }

  save() {
    let result: any = [];
    this.options.forEach((p) => {
      if (p.value == undefined) return;
      let value = p.type == 'date' ? p.value.toISOString() : p.value;
      result.push({ key: p.label, value: Array.isArray(value) ? value : [value] });
    });
    this.change.emit(result);

  }
  onChange(event: any) {
    event.stopPropagation();
    this.save();
  }

  onToggleChange() {
    this.save();
  }

  onDynamicSearch(option: any, event: Event) {
    this.dynamicSearchTexts[option.label] = (event.target as HTMLInputElement).value;
  }

  onDynamicPanelClose(option: any) {
    this.dynamicSearchTexts[option.label] = '';
  }

  clearDynamicSearch(option: any) {
    this.dynamicSearchTexts[option.label] = '';
  }

  matchesDynamicSearch(option: any, value: string): boolean {
    const search = (this.dynamicSearchTexts[option.label] || '').toLowerCase();
    return !search || value.toLowerCase().includes(search);
  }

  getLabel(label: string) {
    return label == "criticity" ? "Criticité" : label;
  }

  // Display-only label: an optional `displayLabel` override wins, else the mapped key.
  // Keeps the persisted key (`label`) intact while allowing a different shown text.
  displayLabel(config: any): string {
    return config?.displayLabel ?? this.getLabel(config?.label);
  }

  public reset() {
    this.options.forEach(option => option.value = undefined);
  }
}
